import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { reportAPI } from '@/services/api';
import { Flag, X } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const REASONS = ['spam', 'fraud', 'inappropriate', 'fake_listing', 'stolen_animal_welfare', 'other'];

/**
 * Universal "Report" button.
 * Props:
 *  - targetType: "ad" | "user"
 *  - targetId: id (or slug) of the target
 *  - adKind: "horse" | "product" | "transport" | "school" | "stable" (required when targetType == "ad")
 *  - variant: "icon" | "link" (visual style)
 */
export default function ReportButton({ targetType, targetId, adKind, variant = 'link' }) {
	const { t } = useTranslation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [reason, setReason] = useState('');
	const [details, setDetails] = useState('');
	const [submitting, setSubmitting] = useState(false);

	const handleClick = () => {
		if (!user) { navigate('/auth'); return; }
		setOpen(true);
	};

	const submit = async () => {
		if (!reason) { toast.error(t('report.reason_required')); return; }
		if (!details.trim()) { toast.error(t('report.details_required')); return; }
		setSubmitting(true);
		try {
			await reportAPI.create({
				target_type: targetType,
				target_id: targetId,
				ad_kind: targetType === 'ad' ? adKind : undefined,
				reason,
				details: details.trim(),
			});
			toast.success(t('report.submitted'));
			setOpen(false);
			setReason('');
			setDetails('');
		} catch (e) {
			const msg = e?.response?.data?.detail || t('report.error');
			toast.error(msg);
		}
		setSubmitting(false);
	};

	const buttonClasses = variant === 'icon'
		? 'inline-flex items-center justify-center p-2 rounded-sm border border-[#E6E2D6] text-[#666055] hover:text-[#9B2C2C] hover:border-[#9B2C2C] transition-colors'
		: 'inline-flex items-center gap-1 text-xs bg-[#9B2C2C] hover:bg-[#7B1F1F] text-white rounded-sm btn-equi p-1';

	return (
		<>
			<button
				type="button"
				onClick={handleClick}
				className={buttonClasses}
				title={t('report.button')}
				data-testid={`report-${targetType}-btn`}
			>
				<Flag className="w-4 h-4" />
				{variant !== 'icon' && <span>{t('report.button')}</span>}
			</button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="sm:max-w-md" data-testid="report-dialog">
					<DialogHeader>
						<DialogTitle className="text-[#1A3C34] font-['Playfair_Display']">
							{targetType === 'ad' ? t('report.dialog_title_ad') : t('report.dialog_title_user')}
						</DialogTitle>
					</DialogHeader>
					<div className="space-y-4 pt-2">
						<p className="text-xs text-[#666055]">{t('report.dialog_help')}</p>
						<div>
							<label className="text-xs font-semibold text-[#666055] uppercase tracking-wider block mb-1">
								{t('report.reason_label')}
							</label>
							<Select value={reason} onValueChange={setReason}>
								<SelectTrigger className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm h-11"
															 data-testid="report-reason-select">
									<SelectValue placeholder={t('report.reason_placeholder')} />
								</SelectTrigger>
								<SelectContent>
									{REASONS.map((r) => (
										<SelectItem key={r} value={r}>{t(`report.reason.${r}`)}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div>
							<label className="text-xs font-semibold text-[#666055] uppercase tracking-wider block mb-1">
								{t('report.details_label')}
							</label>
							<Textarea
								value={details}
								onChange={(e) => setDetails(e.target.value)}
								placeholder={t('report.details_placeholder')}
								rows={4}
								maxLength={1000}
								className="bg-[#F9F7F2] border-[#E6E2D6] rounded-sm"
								data-testid="report-details-input"
							/>
							<p className="text-[10px] text-[#666055]/70 mt-1 text-right">{details.length}/1000</p>
						</div>
						<div className="flex justify-end gap-2 pt-2">
							<Button variant="ghost" onClick={() => setOpen(false)} className="text-[#666055]">
								<X className="w-4 h-4 mr-1" /> {t('common.cancel')}
							</Button>
							<Button
								onClick={submit}
								disabled={submitting || !reason || !details.trim()}
								className="bg-[#9B2C2C] hover:bg-[#7B1F1F] text-white rounded-sm btn-equi"
								data-testid="report-submit-btn"
							>
								<Flag className="w-4 h-4 mr-1" />
								{submitting ? t('common.loading') : t('report.submit')}
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}